import * as React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { FeedbackOption } from './FeedbackOption';
import { FEEDBACK_OPTIONS, type FeedbackVariant } from './feedbackOptions';
import closeIcon from '@/assets/images/svg/close_icon.svg';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: FeedbackVariant;
  onSubmit: (selectedOptions: string[], additionalText: string) => void;
}

/**
 * Modal for collecting detailed feedback on echo message ratings.
 * Displays variant-specific feedback options based on rating type (star, dislike, like).
 * Renders as portal at document body level to escape stacking contexts.
 *
 * Variants:
 * - star: Positive feedback (Perfect voice, Best answer, Very helpful, etc.)
 * - dislike: Negative feedback (Not accurate, Doesn't sound like them, etc.)
 * - like: General positive feedback (Interesting, Engaging, Funny, etc.)
 *
 * @param {FeedbackModalProps} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {() => void} props.onClose - Callback to close modal without submitting
 * @param {FeedbackVariant} props.variant - Feedback type determining displayed options
 * @param {(selectedOptions: string[], additionalText: string) => void} props.onSubmit - Callback with selected option IDs and free text
 */
export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  variant,
  onSubmit,
}) => {
  const [selectedOptions, setSelectedOptions] = React.useState<Set<string>>(new Set());
  const [additionalText, setAdditionalText] = React.useState('');

  const options = FEEDBACK_OPTIONS[variant];

  /**
   * Toggles feedback option selection.
   * Maintains set of selected option IDs for multi-select functionality.
   *
   * @param {string} id - Unique option identifier
   * @param {boolean} checked - Whether option is now checked
   */
  const handleOptionChange = (id: string, checked: boolean) => {
    setSelectedOptions((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  /**
   * Submits selected feedback options and additional text.
   * Resets modal state and closes after submission.
   */
  const handleSubmit = () => {
    onSubmit(Array.from(selectedOptions), additionalText);
    setSelectedOptions(new Set());
    setAdditionalText('');
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className='fixed inset-0 z-top flex items-center justify-center bg-black/50'>
      <div
        className='relative mx-4 w-full max-w-[380px]'
        style={{
          borderRadius: '20px',
          border: '2px solid #D6D1E8',
          background: 'linear-gradient(180deg, #EEEAFF -80.84%, #FFFFFF 102.88%)',
          padding: '20px 24px',
        }}
      >
        <div className='mb-4 flex items-center justify-between'>
          <h2
            className='font-proxima text-[20px] font-bold leading-[25px] tracking-normal'
            style={{
              background: 'linear-gradient(90deg, #1A1036 49.53%, #462C8E 95.55%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Feedback
          </h2>

          <button
            onClick={onClose}
            className='flex size-8 items-center justify-center rounded-full transition-colors hover:bg-black/10'
            aria-label='Close'
          >
            <img src={closeIcon} alt='Close' className='size-6' />
          </button>
        </div>

        <div className='flex flex-col gap-3'>
          {options.map((option) => (
            <FeedbackOption
              key={option.id}
              id={option.id}
              label={option.label}
              checked={selectedOptions.has(option.id)}
              onChange={handleOptionChange}
            />
          ))}
        </div>

        <div className='mt-5 flex justify-center'>
          <Button
            onClick={handleSubmit}
            disabled={selectedOptions.size === 0}
            variant='settings-modify'
            width={235}
            height={49}
            borderRadius={30}
            borderWidth={3}
            fontSize={20}
            fontWeight={600}
            lineHeight={27}
            textColor='#1A1036'
          >
            Send
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
